import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import styles from "./AnalyticsPage.module.css";

/* ────────────────────────────────────────────────────────────
   NOTE ON BACKEND CONTRACT

   /summary is used exactly as documented:
     { range: { key, start, end }, total, byEventType: {...} }

   /timeseries's exact bucket shape wasn't specified in the brief
   (only "returns bucketed event data by eventType"), so
   `normalizeTimeseries` below is defensive: it accepts an array
   directly, or an array under `.data` / `.buckets` / `.series`,
   and reads each bucket's date from `date` / `bucket` / `timestamp`
   / `period`, and its counts either flat on the bucket or nested
   under `.byEventType`. If the real response differs, only that
   function needs to change — nothing else in the page depends on
   the raw shape.

   The business lookup is normalized the same way in `extractBusiness`
   below — it no longer assumes `res.data.name` and instead walks the
   common wrapper shapes (`data.data`, `data.business`, `data.cafe`,
   `data.hotel`) before giving up.
   ──────────────────────────────────────────────────────────── */

const RANGE_OPTIONS = [
    { key: "today", label: "Today" },
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "90d", label: "90 days" },
    { key: "year", label: "This year" },
    { key: "custom", label: "Custom" },
];

const RANGE_PERIOD_LABEL = {
    today: "Today",
    "7d": "Last 7 days",
    "30d": "Last 30 days",
    "90d": "Last 90 days",
    year: "This year",
};

/* Per-metric identity. Color is only used where it communicates
   meaning (an action's identity) — kept muted, never saturated. */
const EVENT_META = {
    whatsapp_click: { label: "WhatsApp", var: "--ap-whatsapp", soft: "--ap-whatsapp-soft" },
    phone_click: { label: "Phone", var: "--ap-phone", soft: "--ap-phone-soft" },
    directions_click: { label: "Directions", var: "--ap-directions", soft: "--ap-directions-soft" },
    booking_click: { label: "Bookings", var: "--ap-bookings", soft: "--ap-bookings-soft" },
    menu_click: { label: "Menu", var: "--ap-menu", soft: "--ap-menu-soft" },
};
const VIEW_META = { label: "Views", var: "--ap-views", soft: "--ap-views-soft" };
const TOTAL_COLOR = "#00864a";

function getGranularity(rangeKey, start, end) {
    if (rangeKey === "today") return "hour";
    if (rangeKey === "7d" || rangeKey === "30d") return "day";
    if (rangeKey === "90d") return "week";
    if (rangeKey === "year") return "month";
    if (rangeKey === "custom" && start && end) {
        const diffDays = (new Date(end) - new Date(start)) / 86400000;
        if (diffDays <= 2) return "hour";
        if (diffDays <= 45) return "day";
        if (diffDays <= 180) return "week";
        return "month";
    }
    return "day";
}

function normalizeTimeseries(raw) {
    if (!raw) return [];
    const buckets = Array.isArray(raw)
        ? raw
        : raw.buckets || raw.data || raw.series || [];
    if (!Array.isArray(buckets)) return [];

    return buckets.map((b) => {
        const src = b.byEventType || b;
        const view = Number(src.view ?? b.view ?? 0);
        const whatsapp_click = Number(src.whatsapp_click ?? 0);
        const phone_click = Number(src.phone_click ?? 0);
        const directions_click = Number(src.directions_click ?? 0);
        const menu_click = Number(src.menu_click ?? 0);
        const booking_click = Number(src.booking_click ?? 0);
        const rawLabel = b.date || b.bucket || b.timestamp || b.period || b._id || "";
        return {
            rawLabel,
            view,
            whatsapp_click,
            phone_click,
            directions_click,
            menu_click,
            booking_click,
            total:
                b.total !== undefined
                    ? Number(b.total)
                    : view + whatsapp_click + phone_click + directions_click + menu_click + booking_click,
        };
    });
}

/* Walks the common API wrapper shapes instead of assuming a flat
   `res.data.name`. Handles: a flat object, `{ data: {...} }`,
   `{ business: {...} }`, and the cafe/hotel-specific wrappers the
   admin's other pages use (`{ cafe: {...} }`, `{ hotel: {...} }`). */
function extractBusiness(raw, businessType) {
    if (!raw || typeof raw !== "object") return null;
    const candidates = [
        raw,
        raw.data,
        raw.business,
        raw.cafe,
        raw.hotel,
        raw[businessType],
        raw.result,
        raw.item,
    ].filter((c) => c && typeof c === "object");

    for (const c of candidates) {
        const name = c.name || c.title || c.businessName || c.username;
        if (name) {
            const city =
                c.city ||
                c.location?.city ||
                c.address?.city ||
                c.address?.town ||
                null;
            return { name, city };
        }
    }
    return null;
}

function formatLabel(rawLabel, granularity) {
    if (!rawLabel) return "";
    const d = new Date(rawLabel);
    if (isNaN(d.getTime())) return String(rawLabel);
    if (granularity === "hour") {
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    if (granularity === "month") {
        return d.toLocaleDateString([], { month: "short", year: "2-digit" });
    }
    if (granularity === "week") {
        return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatFullDate(rawLabel) {
    if (!rawLabel) return "";
    const d = new Date(rawLabel);
    if (isNaN(d.getTime())) return String(rawLabel);
    return d.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });
}

function formatNumber(n) {
    return new Intl.NumberFormat().format(Number(n) || 0);
}

/* ── icons — minimal, single-weight, matching the restrained UI ── */
const ChevronLeftIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const ChevronDownIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const AlertIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);
const InboxIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
        <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
);

/* ── custom recharts tooltip, styled to match the rest of the page ── */
const ChartTooltip = ({ active, payload, label, colorMap }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className={styles.tooltip}>
            <div className={styles.tooltipLabel}>{label}</div>
            {payload.map((p) => (
                <div className={styles.tooltipRow} key={p.dataKey}>
                    <span className={styles.tooltipRowName}>
                        <span
                            className={styles.tooltipDot}
                            style={{ "--dot-color": colorMap?.[p.dataKey] || p.color }}
                        />
                        {p.name}
                    </span>
                    <span>{formatNumber(p.value)}</span>
                </div>
            ))}
        </div>
    );
};

const AnalyticsPage = () => {
    const { businessType, businessId } = useParams();
    const navigate = useNavigate();

    const [rangeKey, setRangeKey] = useState("30d");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [appliedCustom, setAppliedCustom] = useState({ start: "", end: "" });
    const [customErr, setCustomErr] = useState("");
    const [customOpen, setCustomOpen] = useState(false);
    const customRef = useRef(null);

    const [business, setBusiness] = useState(null);
    const [businessLoading, setBusinessLoading] = useState(true);

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [summaryError, setSummaryError] = useState(null);

    const [timeseries, setTimeseries] = useState([]);
    const [timeseriesLoading, setTimeseriesLoading] = useState(true);
    const [timeseriesError, setTimeseriesError] = useState(null);

    const [showViews, setShowViews] = useState(false);

    const granularity = getGranularity(rangeKey, appliedCustom.start, appliedCustom.end);

    /* close the custom-range popover on outside click */
    useEffect(() => {
        if (!customOpen) return;
        const onClick = (e) => {
            if (customRef.current && !customRef.current.contains(e.target)) {
                setCustomOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [customOpen]);

    /* ── business name/type header ── */
    useEffect(() => {
        let ignore = false;
        setBusinessLoading(true);
        axios
            .get(`https://api.moroccool.com/api/${businessType}s/find/${businessId}`)
            .then((res) => {
                if (!ignore) setBusiness(extractBusiness(res.data, businessType));
            })
            .catch(() => {
                if (!ignore) setBusiness(null);
            })
            .finally(() => {
                if (!ignore) setBusinessLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, [businessType, businessId]);

    /* ── analytics fetch ── */
    const fetchAnalytics = useCallback(async () => {
        if (rangeKey === "custom" && (!appliedCustom.start || !appliedCustom.end)) return;

        const params =
            rangeKey === "custom"
                ? { range: "custom", startDate: appliedCustom.start, endDate: appliedCustom.end }
                : { range: rangeKey };

        setSummaryLoading(true);
        setSummaryError(null);
        setTimeseriesLoading(true);
        setTimeseriesError(null);

        try {
            const res = await axios.get(
                `https://api.moroccool.com/api/analytics/${businessType}/${businessId}/summary`,
                { params }
            );
            setSummary(res.data);
        } catch (err) {
            setSummaryError(err?.response?.data?.message || "Couldn't load the summary.");
        } finally {
            setSummaryLoading(false);
        }

        try {
            const res = await axios.get(
                `https://api.moroccool.com/api/analytics/${businessType}/${businessId}/timeseries`,
                { params: { ...params, granularity: getGranularity(rangeKey, appliedCustom.start, appliedCustom.end) } }
            );
            setTimeseries(normalizeTimeseries(res.data));
        } catch (err) {
            setTimeseriesError(err?.response?.data?.message || "Couldn't load the chart data.");
        } finally {
            setTimeseriesLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessType, businessId, rangeKey, appliedCustom.start, appliedCustom.end]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const handleRangeSelect = (key) => {
        setCustomErr("");
        if (key === "custom") {
            setRangeKey("custom");
            setCustomOpen(true);
            return;
        }
        setRangeKey(key);
        setAppliedCustom({ start: "", end: "" });
        setCustomOpen(false);
    };

    const handleApplyCustom = () => {
        if (!customStart || !customEnd) {
            setCustomErr("Pick a start and end date.");
            return;
        }
        if (new Date(customStart) > new Date(customEnd)) {
            setCustomErr("Start date must be before end date.");
            return;
        }
        setCustomErr("");
        setAppliedCustom({ start: customStart, end: customEnd });
        setCustomOpen(false);
    };

    const chartData = useMemo(
        () =>
            timeseries.map((b) => ({
                ...b,
                label: formatLabel(b.rawLabel, granularity),
            })),
        [timeseries, granularity]
    );

    /* action performance: count + share of total non-view actions */
    const actionPerf = useMemo(() => {
        if (!summary) return [];
        const rows = Object.entries(EVENT_META).map(([key, meta]) => ({
            key,
            label: meta.label,
            varName: meta.var,
            value: Number(summary.byEventType?.[key] ?? 0),
        }));
        const total = rows.reduce((s, r) => s + r.value, 0);
        return rows
            .map((r) => ({ ...r, pct: total > 0 ? (r.value / total) * 100 : 0 }))
            .sort((a, b) => b.value - a.value);
    }, [summary]);

    const donutTotal = useMemo(
        () => actionPerf.reduce((s, r) => s + r.value, 0),
        [actionPerf]
    );

    const donutData = useMemo(
        () => actionPerf.filter((r) => r.value > 0),
        [actionPerf]
    );

    /* period label shown under the date control */
    const periodLabel = useMemo(() => {
        if (rangeKey === "custom" && appliedCustom.start && appliedCustom.end) {
            return `${formatFullDate(appliedCustom.start)} – ${formatFullDate(appliedCustom.end)}`;
        }
        return RANGE_PERIOD_LABEL[rangeKey] || "";
    }, [rangeKey, appliedCustom]);

    /* insights: computed only from data actually returned by the API —
       nothing here is estimated or invented. */
    const insights = useMemo(() => {
        const list = [];
        if (summary && donutTotal > 0) {
            const top = actionPerf[0];
            if (top && top.value > 0) {
                list.push(
                    <>
                        <strong>{top.label}</strong> is the most used contact action, with{" "}
                        <strong>{formatNumber(top.value)}</strong> {top.value === 1 ? "click" : "clicks"}{" "}
                        ({top.pct.toFixed(0)}% of all actions).
                    </>
                );
            }
        }
        if (chartData.length > 0) {
            const peak = chartData.reduce((max, b) => (b.total > max.total ? b : max), chartData[0]);
            if (peak.total > 0) {
                list.push(
                    <>
                        Engagement peaked on <strong>{formatFullDate(peak.rawLabel)}</strong>, with{" "}
                        <strong>{formatNumber(peak.total)}</strong> interactions.
                    </>
                );
            }
        }
        if (summary && Number(summary.byEventType?.view) > 0) {
            list.push(
                <>
                    Views generated <strong>{formatNumber(summary.byEventType.view)}</strong>{" "}
                    {Number(summary.byEventType.view) === 1 ? "interaction" : "interactions"} in this period.
                </>
            );
        }
        return list;
    }, [summary, actionPerf, donutTotal, chartData]);

    const businessName = business?.name || null;
    const businessTypeLabel = businessType
        ? businessType.charAt(0).toUpperCase() + businessType.slice(1)
        : "";

    const isEmpty =
        !summaryLoading &&
        !summaryError &&
        summary &&
        Number(summary.total) === 0;

    return (
        <div className={styles.layout}>
            <Sidebar />
            <div className={styles.mainContent}>
                <Navbar />
                <div className={styles.page}>
                    {/* ── breadcrumb ── */}
                    <div className={styles.breadcrumb}>
                        <span>Dashboard</span>
                        <span className={styles.breadcrumbSep}>/</span>
                        <Link to={`/${businessType}s`} className={styles.breadcrumbLink}>
                            {businessType === "cafe" ? "Cafés" : "Hotels"}
                        </Link>
                        <span className={styles.breadcrumbSep}>/</span>
                        <span className={styles.breadcrumbCurrent}>
                            {businessLoading ? "…" : businessName || "Analytics"}
                        </span>
                    </div>

                    {/* ── header ── */}
                    <div className={styles.headerRow}>
                        <div className={styles.identity}>
                            <button className={styles.backLink} onClick={() => navigate(-1)}>
                                <ChevronLeftIcon />
                                <span>{businessType === "cafe" ? "Cafés" : "Hotels"}</span>
                            </button>

                            <h1 className={styles.h1}>
                                {businessLoading ? (
                                    <span className={styles.titleSkel} />
                                ) : (
                                    businessName || "Untitled business"
                                )}
                            </h1>
                            <p className={styles.identitySub}>Engagement &amp; performance</p>
                            <div className={styles.eyebrow}>{businessTypeLabel}</div>
                        </div>

                        <div className={styles.rangeControl} ref={customRef}>
                            <div className={styles.rangeSeg}>
                                {RANGE_OPTIONS.map((opt) => (
                                    <button
                                        key={opt.key}
                                        className={`${styles.rangeBtn} ${rangeKey === opt.key ? styles.rangeBtnActive : ""}`}
                                        onClick={() => handleRangeSelect(opt.key)}
                                    >
                                        {opt.label}
                                        {opt.key === "custom" && <ChevronDownIcon />}
                                    </button>
                                ))}
                            </div>

                            {periodLabel && (
                                <div className={styles.periodLabel}>
                                    <strong>{periodLabel}</strong>
                                </div>
                            )}

                            {customOpen && (
                                <div className={styles.customPop}>
                                    <div className={styles.customRow}>
                                        <input
                                            type="date"
                                            className={styles.dateInput}
                                            value={customStart}
                                            max={customEnd || undefined}
                                            onChange={(e) => setCustomStart(e.target.value)}
                                        />
                                        <span className={styles.customTo}>–</span>
                                        <input
                                            type="date"
                                            className={styles.dateInput}
                                            value={customEnd}
                                            min={customStart || undefined}
                                            onChange={(e) => setCustomEnd(e.target.value)}
                                        />
                                    </div>
                                    {customErr && <span className={styles.customErr}>{customErr}</span>}
                                    <button className={styles.applyBtn} onClick={handleApplyCustom}>
                                        Apply range
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── KPIs ── */}
                    {summaryError ? (
                        <div className={styles.errorState}>
                            <AlertIcon />
                            <div className={styles.errorTitle}>Unable to load analytics</div>
                            <div className={styles.errorSub}>{summaryError}</div>
                            <button className={styles.retryBtn} onClick={fetchAnalytics}>
                                Retry
                            </button>
                        </div>
                    ) : (
                        <div className={styles.kpiSection}>
                            <div className={styles.kpiPrimary}>
                                <div className={styles.kpiPrimaryLabel}>Total interactions</div>
                                {summaryLoading ? (
                                    <>
                                        <div className={styles.kpiSkelLine} style={{ width: "70%", height: 34 }} />
                                        <div className={styles.kpiSkelLine} style={{ width: "45%" }} />
                                    </>
                                ) : (
                                    <>
                                        <div className={styles.kpiPrimaryValue}>{formatNumber(summary?.total)}</div>
                                        <div className={`${styles.kpiTrend} ${styles.kpiTrendMuted}`}>
                                            {periodLabel || "Selected period"}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className={styles.kpiSecondaryRow}>
                                {[
                                    { key: "view", label: "Views", varName: VIEW_META.var },
                                    { key: "whatsapp_click", label: "WhatsApp", varName: EVENT_META.whatsapp_click.var },
                                    { key: "phone_click", label: "Phone", varName: EVENT_META.phone_click.var },
                                    { key: "directions_click", label: "Directions", varName: EVENT_META.directions_click.var },
                                    { key: "booking_click", label: "Bookings", varName: EVENT_META.booking_click.var },
                                ].map(({ key, label, varName }) => (
                                    <div
                                        className={styles.kpiCard}
                                        key={key}
                                        style={{ "--kpi-accent": `var(${varName})` }}
                                    >
                                        {summaryLoading ? (
                                            <>
                                                <div className={styles.kpiSkelIcon} />
                                                <div className={styles.kpiSkelLine} style={{ width: "50%", height: 20 }} />
                                            </>
                                        ) : (
                                            <>
                                                <div className={styles.kpiLbl}>
                                                    <span className={styles.kpiDot} />
                                                    {label}
                                                </div>
                                                <div className={styles.kpiN}>
                                                    {formatNumber(summary?.byEventType?.[key])}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── empty state ── */}
                    {isEmpty && (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIc}>
                                <InboxIcon />
                            </div>
                            <div className={styles.emptyT}>No engagement data yet</div>
                            <div className={styles.emptyS}>
                                Once visitors interact with this business profile, performance data will appear here.
                            </div>
                        </div>
                    )}

                    {!isEmpty && !summaryError && (
                        <>
                            {/* ── engagement over time ── */}
                            <div className={styles.section}>
                                <div className={styles.sectionHead}>
                                    <div>
                                        <h2 className={styles.sectionTitle}>Engagement over time</h2>
                                        {!summaryLoading && (
                                            <div className={styles.sectionValue}>
                                                {formatNumber(summary?.total)} interactions
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {timeseriesLoading ? (
                                    <div className={styles.chartSkel} />
                                ) : timeseriesError ? (
                                    <div className={styles.chartErr}>
                                        <AlertIcon />
                                        <span>{timeseriesError}</span>
                                        <button className={styles.retryBtnSm} onClick={fetchAnalytics}>
                                            Retry
                                        </button>
                                    </div>
                                ) : chartData.length === 0 ? (
                                    <div className={styles.chartEmpty}>No data for this range.</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={TOTAL_COLOR} stopOpacity={0.16} />
                                                    <stop offset="100%" stopColor={TOTAL_COLOR} stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="0" stroke="#eeede9" vertical={false} />
                                            <XAxis
                                                dataKey="label"
                                                tick={{ fontSize: 11, fill: "#b7b5af", fontWeight: 500 }}
                                                axisLine={false}
                                                tickLine={false}
                                                tickMargin={10}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 11, fill: "#b7b5af", fontWeight: 500 }}
                                                axisLine={false}
                                                tickLine={false}
                                                allowDecimals={false}
                                                width={40}
                                            />
                                            <Tooltip
                                                content={<ChartTooltip colorMap={{ total: TOTAL_COLOR }} />}
                                                cursor={{ stroke: "#dbd8d1", strokeWidth: 1 }}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="total"
                                                name="Interactions"
                                                stroke={TOTAL_COLOR}
                                                strokeWidth={2}
                                                fill="url(#totalGrad)"
                                                activeDot={{ r: 4, strokeWidth: 0 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )}
                            </div>

                            {/* ── secondary: action performance + engagement mix ── */}
                            <div className={styles.twoCol}>
                                <div className={styles.section}>
                                    <div className={styles.sectionHead}>
                                        <div>
                                            <h2 className={styles.sectionTitle}>Action performance</h2>
                                            <p className={styles.sectionSub}>Share of each action type</p>
                                        </div>
                                    </div>
                                    {summaryLoading ? (
                                        <div className={styles.chartSkel} />
                                    ) : (
                                        <div className={styles.perfList}>
                                            {actionPerf.map((row) => (
                                                <div className={styles.perfRow} key={row.key}>
                                                    <div className={styles.perfRowTop}>
                                                        <span className={styles.perfName}>
                                                            <span
                                                                className={styles.kpiDot}
                                                                style={{ "--kpi-accent": `var(${row.varName})` }}
                                                            />
                                                            {row.label}
                                                        </span>
                                                        <span className={styles.perfStats}>
                                                            {formatNumber(row.value)}
                                                            <span className={styles.perfPct}>{row.pct.toFixed(0)}%</span>
                                                        </span>
                                                    </div>
                                                    <div className={styles.perfBarTrack}>
                                                        <div
                                                            className={styles.perfBarFill}
                                                            style={{
                                                                width: `${row.pct}%`,
                                                                "--bar-color": `var(${row.varName})`,
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className={styles.section}>
                                    <div className={styles.sectionHead}>
                                        <div>
                                            <h2 className={styles.sectionTitle}>Engagement mix</h2>
                                            <p className={styles.sectionSub}>All recorded actions</p>
                                        </div>
                                    </div>
                                    {summaryLoading ? (
                                        <div className={styles.chartSkel} />
                                    ) : donutData.length === 0 ? (
                                        <div className={styles.chartEmpty}>No actions recorded yet.</div>
                                    ) : (
                                        <div className={styles.donutWrap}>
                                            <div className={styles.donutCenter}>
                                                <ResponsiveContainer width={200} height={200}>
                                                    <PieChart>
                                                        <Pie
                                                            data={donutData}
                                                            dataKey="value"
                                                            nameKey="label"
                                                            innerRadius={64}
                                                            outerRadius={90}
                                                            paddingAngle={2}
                                                            stroke="none"
                                                        >
                                                            {donutData.map((entry) => (
                                                                <Cell key={entry.key} fill={`var(${entry.varName})`} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip content={<ChartTooltip />} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <div className={styles.donutCenterInner}>
                                                    <div className={styles.donutCenterN}>{formatNumber(donutTotal)}</div>
                                                    <div className={styles.donutCenterL}>Actions</div>
                                                </div>
                                            </div>
                                            <div className={styles.donutLegend}>
                                                {donutData.map((row) => (
                                                    <span className={styles.legendItem} key={row.key}>
                                                        <span
                                                            className={styles.legendDot}
                                                            style={{ "--dot-color": `var(${row.varName})` }}
                                                        />
                                                        {row.label}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ── actions over time ── */}
                            <div className={styles.section}>
                                <div className={styles.sectionHead}>
                                    <div>
                                        <h2 className={styles.sectionTitle}>Actions over time</h2>
                                        <p className={styles.sectionSub}>
                                            WhatsApp, phone, directions &amp; bookings by {granularity}
                                        </p>
                                    </div>
                                    <div className={styles.legendRow}>
                                        {Object.entries(EVENT_META)
                                            .filter(([key]) => key !== "menu_click")
                                            .map(([key, meta]) => (
                                                <span className={styles.legendItem} key={key}>
                                                    <span
                                                        className={styles.legendDot}
                                                        style={{ "--dot-color": `var(${meta.var})` }}
                                                    />
                                                    {meta.label}
                                                </span>
                                            ))}
                                        <label className={styles.viewsToggle}>
                                            <input
                                                type="checkbox"
                                                checked={showViews}
                                                onChange={(e) => setShowViews(e.target.checked)}
                                            />
                                            Show views
                                        </label>
                                    </div>
                                </div>
                                {timeseriesLoading ? (
                                    <div className={styles.chartSkel} />
                                ) : timeseriesError ? (
                                    <div className={styles.chartErr}>
                                        <AlertIcon />
                                        <span>{timeseriesError}</span>
                                        <button className={styles.retryBtnSm} onClick={fetchAnalytics}>
                                            Retry
                                        </button>
                                    </div>
                                ) : chartData.length === 0 ? (
                                    <div className={styles.chartEmpty}>No data for this range.</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <LineChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="0" stroke="#eeede9" vertical={false} />
                                            <XAxis
                                                dataKey="label"
                                                tick={{ fontSize: 11, fill: "#b7b5af", fontWeight: 500 }}
                                                axisLine={false}
                                                tickLine={false}
                                                tickMargin={10}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 11, fill: "#b7b5af", fontWeight: 500 }}
                                                axisLine={false}
                                                tickLine={false}
                                                allowDecimals={false}
                                                width={40}
                                            />
                                            <Tooltip
                                                content={
                                                    <ChartTooltip
                                                        colorMap={{
                                                            whatsapp_click: "#1f7a4d",
                                                            phone_click: "#3e6fa6",
                                                            directions_click: "#b8863c",
                                                            booking_click: "#8c6a4f",
                                                            view: "#6b6b66",
                                                        }}
                                                    />
                                                }
                                                cursor={{ stroke: "#dbd8d1", strokeWidth: 1 }}
                                            />
                                            {Object.entries(EVENT_META).map(([key, meta]) =>
                                                key === "menu_click" ? null : (
                                                    <Line
                                                        key={key}
                                                        type="monotone"
                                                        dataKey={key}
                                                        name={meta.label}
                                                        stroke={`var(${meta.var})`}
                                                        strokeWidth={2}
                                                        dot={false}
                                                        activeDot={{ r: 3.5, strokeWidth: 0 }}
                                                    />
                                                )
                                            )}
                                            {showViews && (
                                                <Line
                                                    type="monotone"
                                                    dataKey="view"
                                                    name="Views"
                                                    stroke="#b7b5af"
                                                    strokeWidth={1.5}
                                                    strokeDasharray="3 3"
                                                    dot={false}
                                                    activeDot={{ r: 3, strokeWidth: 0 }}
                                                />
                                            )}
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </div>

                            {/* ── insights ── */}
                            {insights.length > 0 && (
                                <div className={styles.section}>
                                    <div className={styles.sectionHead}>
                                        <h2 className={styles.sectionTitle}>Performance insights</h2>
                                    </div>
                                    <div className={styles.insightsGrid}>
                                        {insights.map((text, i) => (
                                            <div className={styles.insightCard} key={i}>
                                                <span className={styles.insightDot} />
                                                <div className={styles.insightText}>{text}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnalyticsPage;